// src/utils/fantacalcioLeagueRosters.js
// Parsing dell'export "Esporta rose" di Leghe Fantacalcio (xlsx/csv/testo),
// incrocio con la propria rosa e statistiche di lega (asta a listone → tab
// "Lega").
//
// La pagina rose (leghe.fantacalcio.it/<lega>/view/rosters/) richiede login e
// non è raggiungibile né via CORS né server-side: l'utente carica il file
// esportato (o incolla il testo copiato) e il parsing avviene qui, lato client.
//
// Formato dell'export: blocchi di 3 colonne per squadra
//   [ NomeSquadra | "costo" | (vuota) ]
// una riga di intestazione con i nomi squadra + "costo", poi ~25 righe
// giocatore [ nome | costo | "" ], poi una riga "totale <spesa>", poi una riga
// vuota; il blocco si ripete in orizzontale (di solito 12 squadre) e in
// verticale.
import {
  normalizeForMatch,
  getAutoMatchId,
  getListoneEntryById,
  getSuggestedCandidates,
} from './legheFantacalcioExport';

const TEAM_BLOCK_WIDTH = 3; // nome | costo | vuota

function cleanPlayerName(raw = '') {
  // Fantacalcio marca alcuni giocatori con un "*" finale (es. "Anjorin *")
  return String(raw).replace(/\s*\*+\s*$/, '').replace(/\s+/g, ' ').trim();
}

function toCost(raw) {
  const n = Number(String(raw ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

// Reparto classic (P/D/C/A) da un ruolo classic o da token Mantra ("Dd/Ds/E").
export function roleBucketOf(role = '') {
  const tokens = String(role || '').split('/').map((t) => t.trim());
  if (tokens.some((t) => /^p$|^por$/i.test(t))) return 'P';
  if (tokens.some((t) => ['Dd', 'Dc', 'Ds', 'B', 'D'].includes(t))) return 'D';
  if (tokens.some((t) => ['E', 'M', 'C', 'W', 'T'].includes(t))) return 'C';
  if (tokens.some((t) => ['A', 'Pc'].includes(t))) return 'A';
  return 'A';
}

// rows: array di array. Ritorna { teams: {[team]: [{name, cost}]}, order: [...], totals: {[team]: number} }
export function parseRostersRows(rows = []) {
  const teams = {};
  const totals = {};
  const order = [];
  let activeCols = null;

  (rows || []).forEach((row) => {
    if (!Array.isArray(row)) return;

    const headers = [];
    for (let c = 0; c < row.length; c += TEAM_BLOCK_WIDTH) {
      const label = String(row[c + 1] ?? '').trim().toLowerCase();
      const teamName = String(row[c] ?? '').trim();
      if (label === 'costo' && teamName) headers.push({ col: c, team: teamName });
    }

    if (headers.length) {
      activeCols = headers;
      headers.forEach((h) => {
        if (!teams[h.team]) {
          teams[h.team] = [];
          order.push(h.team);
        }
      });
      return;
    }

    if (!activeCols) return;

    activeCols.forEach((h) => {
      const raw = String(row[h.col] ?? '').trim();
      if (!raw) return;
      if (raw.toLowerCase() === 'totale') {
        totals[h.team] = toCost(row[h.col + 1]);
        return;
      }
      const name = cleanPlayerName(raw);
      if (!name) return;
      teams[h.team].push({ name, cost: toCost(row[h.col + 1]) });
    });
  });

  return { teams, order, totals };
}

// arrayBuffer da un <input type="file"> (xlsx/xls/csv).
// `xlsx` (SheetJS) è pesante → import dinamico: entra nel bundle solo quando
// l'utente carica davvero un file.
export async function parseRostersArrayBuffer(arrayBuffer) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const merged = {};
  const totals = {};
  const order = [];
  wb.SheetNames.forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
    const parsed = parseRostersRows(rows);
    Object.entries(parsed.teams).forEach(([team, players]) => {
      if (!merged[team]) { merged[team] = []; order.push(team); }
      merged[team] = merged[team].concat(players);
    });
    Object.assign(totals, parsed.totals);
  });
  return { teams: merged, order, totals };
}

// ─── Rosa di UNA squadra ("Esporta rosa" della singola squadra) ────────────
// Formato: intestazione "Ruolo;Nome;Squadra;Costo d'acquisto;Quotazione
// Attuale;...", una riga per giocatore. Il nome della squadra NON è nel file
// ma nel nome del file: "<lega>_<squadra>_rosa_<data>.csv"
// (es. "fantawembley-the-manager_everton_rosa_2026-10-16.csv" → "Everton").
export function teamNameFromRosterFileName(fileName = '') {
  const base = String(fileName).replace(/^.*[\\/]/, '').replace(/\.[^.]+$/, '');
  const m = base.match(/^[^_]+_(.+?)_rosa(?:_[\d-]+)?$/i);
  const raw = (m ? m[1] : base).replace(/[-_]+/g, ' ').trim();
  return raw.replace(/\b\w/g, (c) => c.toUpperCase()) || 'Squadra';
}

// rows: array di array. Ritorna [{name, cost, club, role}] oppure null se
// l'intestazione non è quella della rosa singola.
export function parseSingleTeamRows(rows = []) {
  const headerIdx = (rows || []).findIndex((row) => {
    const cells = (row || []).map((c) => String(c ?? '').trim().toLowerCase());
    return cells.includes('nome') && cells.some((c) => c.startsWith('costo'));
  });
  if (headerIdx < 0) return null;
  const header = rows[headerIdx].map((c) => String(c ?? '').trim().toLowerCase());
  const col = (pred) => header.findIndex(pred);
  const iName = col((c) => c === 'nome');
  const iCost = col((c) => c.startsWith('costo'));
  const iClub = col((c) => c === 'squadra');
  const iRole = col((c) => c === 'ruolo');
  // "Quotazione Attuale": il prezzo a cui si può VENDERE oggi il giocatore
  // (cost = quanto è stato pagato). La differenza è la plusvalenza.
  const iCurrent = col((c) => c.startsWith('quotazione'));

  const players = [];
  rows.slice(headerIdx + 1).forEach((row) => {
    const name = cleanPlayerName(row?.[iName] ?? '');
    if (!name) return;
    players.push({
      name,
      cost: toCost(row?.[iCost]),
      currentValue: iCurrent >= 0 && String(row?.[iCurrent] ?? '').trim() !== '' ? toCost(row?.[iCurrent]) : null,
      club: iClub >= 0 ? String(row?.[iClub] ?? '').trim() : '',
      // "Dd E" → "Dd/E": stesso formato dei ruoli Mantra nel resto dell'app.
      role: iRole >= 0 ? String(row?.[iRole] ?? '').trim().split(/\s+/).filter(Boolean).join('/') : '',
    });
  });
  return players;
}

function splitCsvLine(line, delimiter) {
  return line.split(delimiter).map((c) => c.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
}

function decodeText(arrayBuffer) {
  const utf8 = new TextDecoder('utf-8').decode(arrayBuffer).replace(/^﻿/, '');
  // File salvati da Excel su Windows: se l'UTF-8 non torna (caratteri
  // sostituiti), si riprova con la codifica Windows.
  if (!utf8.includes('�')) return utf8;
  try { return new TextDecoder('windows-1252').decode(arrayBuffer); } catch { return utf8; }
}

// Punto d'ingresso unico per un file caricato: riconosce da solo
//  - l'export "Esporta rose" di tutta la lega (xlsx/csv) → kind 'league';
//  - la rosa di una singola squadra (csv/xlsx)            → kind 'single'.
// Ritorna sempre { kind, teams, order, totals } (+ team per 'single').
export async function parseRosterFile(file) {
  const fileName = file?.name || '';
  const buffer = await file.arrayBuffer();
  const isText = /\.(csv|txt)$/i.test(fileName);

  let singleRows = null;
  if (isText) {
    const text = decodeText(buffer);
    const lines = text.replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim());
    const first = lines[0] || '';
    const delimiter = first.includes(';') ? ';' : first.includes('\t') ? '\t' : ',';
    singleRows = lines.map((l) => splitCsvLine(l, delimiter));
  } else {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(buffer, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    singleRows = ws ? XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }) : [];
  }

  const single = parseSingleTeamRows(singleRows);
  if (single && single.length) {
    const team = teamNameFromRosterFileName(fileName);
    return {
      kind: 'single',
      team,
      teams: { [team]: single },
      order: [team],
      totals: { [team]: single.reduce((s, p) => s + p.cost, 0) },
    };
  }

  const league = await parseRostersArrayBuffer(buffer);
  return { kind: 'league', ...league };
}

// Unisce un file appena letto alle rose della lega già caricate: l'export di
// tutta la lega SOSTITUISCE, la rosa di una singola squadra si AGGIUNGE (o
// sostituisce solo quella squadra). Usato dal tab Lega e dal pulsante
// "Carica rose" dell'asta a listone.
export function mergeRosterIntoLeague(prev, data, fileName) {
  if (!(data?.order || []).length) return prev;
  if (data.kind === 'single' && prev?.teams) {
    const next = { ...prev, teams: { ...prev.teams }, order: [...(prev.order || [])], totals: { ...(prev.totals || {}) } };
    const team = data.team;
    next.teams[team] = data.teams[team];
    next.totals[team] = data.totals[team];
    if (!next.order.includes(team)) next.order.push(team);
    next.source = next.source && !next.source.includes(fileName) ? `${next.source} + ${fileName}` : fileName;
    return next;
  }
  return { teams: { ...data.teams }, order: [...data.order], totals: { ...(data.totals || {}) }, source: fileName };
}

// Testo incollato (TSV/CSV da "seleziona tutto → copia" della pagina rose)
export function parseRostersText(text = '') {
  const rows = String(text || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.split(/\t|;|,(?=\s*(?:costo|totale|\d))/i).map((c) => c.trim()));
  return parseRostersRows(rows);
}

// ─── Match nomi rosa → export ─────────────────────────────────────────────
// Ritorna i nomi normalizzati con cui cercare `player` nell'export (nome
// diretto FantApp + traduzione canonica del listone Fantacalcio).
export function resolveMatchTargets(player) {
  const targets = new Set();
  const direct = normalizeForMatch(player?.name);
  if (direct) targets.add(direct);

  const autoId = getAutoMatchId(player?.name, player?.team);
  if (autoId) {
    const entry = getListoneEntryById(autoId);
    if (entry?.name) targets.add(normalizeForMatch(entry.name));
  } else {
    getSuggestedCandidates(player?.name, player?.team).forEach((c) => {
      const n = normalizeForMatch(c.name);
      if (n) targets.add(n);
    });
  }
  return [...targets];
}

// Chiave stabile per un giocatore della rosa (per gli override manuali).
export function playerMatchKey(name, team) {
  return `${normalizeForMatch(name)}|${normalizeForMatch(team)}`;
}

// Indice di tutte le "voci" distinte dell'export: norm → nome visualizzato.
export function buildLeagueNameIndex(leagueTeams = {}) {
  const idx = new Map();
  Object.values(leagueTeams).forEach((players) => {
    (players || []).forEach((p) => {
      const n = normalizeForMatch(p.name);
      if (n && !idx.has(n)) idx.set(n, p.name);
    });
  });
  return idx;
}

function nameTokens(value = '') {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3);
}

// Candidati plausibili nell'export per un giocatore che NON ha un match certo.
// Es. rosa "Yan Couto" ↔ export "Couto" (ultimo cognome), "N. Paz" ↔ "Paz N.".
export function findLeagueCandidates(player, leagueNameIndex) {
  const targets = resolveMatchTargets(player);
  const pName = normalizeForMatch(player?.name);
  const pTokens = nameTokens(player?.name);
  const lastTok = pTokens[pTokens.length - 1];
  const firstTok = pTokens[0];
  const out = [];

  leagueNameIndex.forEach((display, norm) => {
    let score = 0;
    if (targets.includes(norm) || norm === pName) score = 100;
    else if (lastTok && (norm === lastTok || norm.startsWith(lastTok) || norm.endsWith(lastTok))) score = 70;
    else if (firstTok && lastTok && firstTok !== lastTok && norm.startsWith(firstTok) && norm.includes(lastTok)) score = 65;
    else if (pName && (norm.startsWith(pName) || pName.startsWith(norm))) score = 55;
    else {
      const overlap = pTokens.filter((t) => nameTokens(display).includes(t)).length;
      if (overlap) score = 25 + overlap * 15;
      else if (lastTok && lastTok.length >= 4 && (norm.includes(lastTok) || lastTok.includes(norm))) score = 20;
    }
    if (score >= 20) out.push({ norm, name: display, score });
  });

  return out.sort((a, b) => b.score - a.score).slice(0, 8);
}

// ─── Export → giocatori del dataset (Simula rosa: "Importa da Excel") ──────
// Indice norm → giocatore del dataset, sui nomi diretti FantApp e sul nome
// canonico del listone Fantacalcio (stessa logica di metaByNorm nel tab Lega).
export function buildExcelNameIndex(players = []) {
  const idx = new Map();
  (players || []).forEach((p) => {
    const direct = normalizeForMatch(p?.name);
    if (direct && !idx.has(direct)) idx.set(direct, p);
    const id = getAutoMatchId(p?.name, p?.team);
    if (id) {
      const entry = getListoneEntryById(id);
      const canon = entry?.name ? normalizeForMatch(entry.name) : '';
      if (canon && !idx.has(canon)) idx.set(canon, p);
    }
  });
  return idx;
}

// Giocatori del dataset plausibili per un nome dell'export non riconosciuto
// (es. "Paz N." → "Nico Paz"): match sul cognome/token più lungo.
export function suggestPlayersForExcelName(excelName, players = [], limit = 6) {
  const tokens = nameTokens(excelName).sort((a, b) => b.length - a.length);
  const main = tokens[0];
  if (!main) return [];
  const out = [];
  (players || []).forEach((p) => {
    const pTokens = nameTokens(p?.name);
    let score = 0;
    if (pTokens.includes(main)) score = 80;
    else if (pTokens.some((t) => t.startsWith(main) || main.startsWith(t))) score = 50;
    else if (normalizeForMatch(p?.name).includes(main)) score = 30;
    if (!score) return;
    score += tokens.slice(1).filter((t) => pTokens.some((pt) => pt.startsWith(t))).length * 10;
    out.push({ player: p, score });
  });
  return out.sort((a, b) => b.score - a.score).slice(0, limit).map((o) => o.player);
}

// Abbina le voci di UNA squadra dell'export ([{name, cost}]) ai giocatori del
// dataset. Ritorna { matched: [{player, cost, excelName}], unmatched:
// [{name, cost, suggestions}] }. Un giocatore del dataset non viene mai
// abbinato due volte.
function sameClub(a, b) {
  const x = normalizeForMatch(a);
  const y = normalizeForMatch(b);
  return Boolean(x && y && (x === y || x.includes(y) || y.includes(x)));
}

// Con la squadra di Serie A nel file (rosa singola) l'abbinamento si fa prima
// tra i giocatori di QUEL club: "Paz N." + Como → Nico Paz senza ambiguità, e
// due omonimi in club diversi non si confondono. Se nel club non c'è (es.
// trasferito e dataset non ancora aggiornato), si ripiega su tutto il listone.
function makeClubResolver(players) {
  const cache = new Map();
  return (club) => {
    const key = normalizeForMatch(club);
    if (!key) return null;
    if (!cache.has(key)) {
      const clubPlayers = (players || []).filter((p) => sameClub(p?.team, club));
      cache.set(key, clubPlayers.length ? { players: clubPlayers, index: buildExcelNameIndex(clubPlayers) } : null);
    }
    return cache.get(key);
  };
}

function resolveEntry(e, globalIndex, clubResolver) {
  const norm = normalizeForMatch(e.name);
  const club = e.club ? clubResolver(e.club) : null;
  if (club) {
    const hit = club.index.get(norm) || pickConfidentGuess(e.name, suggestPlayersForExcelName(e.name, club.players));
    if (hit) return hit;
  }
  return globalIndex.get(norm) || null;
}

export function matchExcelTeamToPlayers(entries = [], players = []) {
  const idx = buildExcelNameIndex(players);
  const clubResolver = makeClubResolver(players);
  const usedIds = new Set();
  const matched = [];
  const unmatched = [];
  (entries || []).forEach((e) => {
    const p = resolveEntry(e, idx, clubResolver);
    if (p && !usedIds.has(String(p.id))) {
      usedIds.add(String(p.id));
      matched.push({ player: p, cost: Number(e.cost) || 0, currentValue: e.currentValue ?? null, excelName: e.name });
    } else {
      const club = e.club ? clubResolver(e.club) : null;
      const clubSuggestions = club ? suggestPlayersForExcelName(e.name, club.players) : [];
      const seen = new Set(clubSuggestions.map((s) => String(s.id)));
      const suggestions = [
        ...clubSuggestions,
        ...suggestPlayersForExcelName(e.name, players).filter((s) => !seen.has(String(s.id))),
      ].filter((s) => !usedIds.has(String(s.id))).slice(0, 6);
      unmatched.push({
        name: e.name,
        cost: Number(e.cost) || 0,
        currentValue: e.currentValue ?? null,
        suggestions,
        guess: pickConfidentGuess(e.name, suggestions),
      });
    }
  });
  return { matched, unmatched };
}

// Abbinamento "sicuro" di un nome dell'export che non corrisponde esattamente
// a nessun giocatore ("Zambo Anguissa" → Anguissa, "Paz N." → Nico Paz):
// null se non c'è un unico candidato coerente. Con `club` (rosa singola) si
// cerca prima tra i giocatori di quel club.
export function guessPlayerForExcelName(excelName, players = [], club = '') {
  if (club) {
    const clubPlayers = (players || []).filter((p) => sameClub(p?.team, club));
    const hit = pickConfidentGuess(excelName, suggestPlayersForExcelName(excelName, clubPlayers));
    if (hit) return hit;
  }
  return pickConfidentGuess(excelName, suggestPlayersForExcelName(excelName, players));
}

// Iniziali del nome nell'export: "Paz N." → ['n'], "Ederson D.S." → ['d','s'].
function excelInitials(name = '') {
  return String(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[\s.]+/)
    .filter((t) => /^[a-z]{1,2}$/.test(t));
}

// Un solo candidato col cognome esatto e coerente con le iniziali (se ci sono):
// "Paz N." → Nico Paz (non "Paz Y"), "Konè M." → Manu Kone, "Couto" → Yan Couto.
// Iniziali incoerenti ("Ederson D.S." vs "Ederson J") → nessuna proposta.
// È solo un pre-riempimento: l'utente lo vede e può cambiarlo.
function pickConfidentGuess(excelName, suggestions = []) {
  const main = nameTokens(excelName).sort((a, b) => b.length - a.length)[0];
  if (!main) return null;
  const initials = excelInitials(excelName);
  const fits = suggestions.filter((p) => {
    const tokens = String(p?.name || '')
      .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/).filter(Boolean);
    if (!tokens.includes(main)) return false;
    const others = tokens.filter((t) => t !== main);
    return initials.every((i) => others.some((t) => t.startsWith(i[0])));
  });
  return fits.length === 1 ? fits[0] : null;
}

// Voci dell'export che NON si risolvono a nessun giocatore del dataset
// (resolvedNorms = Set dei norm già coperti da metaByNorm o dai link manuali).
// Ordinate per quante squadre le hanno.
export function findUnresolvedExcelEntries({ leagueTeams = {}, resolvedNorms = new Set(), limit = 40 }) {
  const byNorm = new Map(); // norm → { name, count }
  Object.values(leagueTeams).forEach((players) => {
    (players || []).forEach((p) => {
      const n = normalizeForMatch(p.name);
      if (!n || resolvedNorms.has(n)) return;
      if (!byNorm.has(n)) byNorm.set(n, { norm: n, name: p.name, count: 0 });
      byNorm.get(n).count += 1;
    });
  });
  return [...byNorm.values()].sort((a, b) => b.count - a.count).slice(0, limit);
}

// Abbinamenti automatici "sicuri" { excelNorm: playerId } per i nomi
// dell'export che non coincidono col dataset ("Zambo Anguissa" → Anguissa,
// "Paz N." → Nico Paz). Esclusi i nomi già decisi dall'utente in playerLinks
// (anche "non mi interessa" = null): le scelte manuali vincono sempre.
export function buildAutoLinks({ leagueTeams = {}, players = [], playerLinks = {} }) {
  const out = {};
  const index = buildExcelNameIndex(players);
  const seen = new Set();
  Object.values(leagueTeams || {}).forEach((list) => (list || []).forEach((e) => {
    const norm = normalizeForMatch(e.name);
    if (!norm || seen.has(norm) || index.has(norm) || norm in (playerLinks || {})) return;
    seen.add(norm);
    const guess = guessPlayerForExcelName(e.name, players, e.club);
    if (guess) out[norm] = String(guess.id);
  }));
  return out;
}

// Possesso in lega per OGNI giocatore del dataset (non solo la propria rosa):
// { countById: Map(playerId → n° squadre della lega che lo hanno), totalTeams }.
// Usato dal tab "Squadra" per annotare il listone. `playerLinks` = { excelNorm:
// playerId | null } dagli abbinamenti manuali del tab "Lega".
export function buildOwnershipCountByPlayerId({ players = [], leagueTeams = {}, playerLinks = {} }) {
  const leagueSets = Object.values(leagueTeams).map((list) => {
    const s = new Set();
    (list || []).forEach((p) => {
      const n = normalizeForMatch(p.name);
      if (n) s.add(n);
    });
    return s;
  });
  const totalTeams = leagueSets.length;

  const linkByPlayerId = new Map();
  const links = { ...buildAutoLinks({ leagueTeams, players, playerLinks }), ...(playerLinks || {}) };
  Object.entries(links).forEach(([norm, pid]) => {
    if (pid == null) return;
    const key = String(pid);
    if (!linkByPlayerId.has(key)) linkByPlayerId.set(key, []);
    linkByPlayerId.get(key).push(norm);
  });

  const countById = new Map();
  if (!totalTeams) return { countById, totalTeams };

  (players || []).forEach((player) => {
    const id = String(player.id);
    const targets = new Set([
      ...resolveMatchTargets(player),
      ...(linkByPlayerId.get(id) || []),
    ]);
    if (!targets.size) return;
    let count = 0;
    for (const teamSet of leagueSets) {
      for (const norm of targets) {
        if (teamSet.has(norm)) { count += 1; break; }
      }
    }
    if (count > 0) countById.set(id, count);
  });

  return { countById, totalTeams };
}

// ─── Incrocio rosa personale ↔ rose della lega ───────────────────────────
// playerLinks: { [excelNorm]: playerId | null }  (associazioni manuali
//   export → giocatore del dataset; null = "ignora").
export function buildOwnershipReport({ rosterPlayers = [], leagueTeams = {}, myTeamName = '', playerLinks = {} }) {
  const myNorm = normalizeForMatch(myTeamName || '');

  const leagueIndex = Object.entries(leagueTeams).map(([team, players]) => ({
    team,
    isMine: Boolean(myNorm) && normalizeForMatch(team) === myNorm,
    byNorm: (players || []).reduce((m, p) => {
      const n = normalizeForMatch(p.name);
      if (n) m.set(n, Number(p.cost || 0));
      return m;
    }, new Map()),
  }));

  const totalTeams = leagueIndex.length;
  const othersCount = leagueIndex.filter((t) => !t.isMine).length;

  // playerId → [excelNorm…] dagli abbinamenti manuali
  const linkByPlayerId = {};
  Object.entries(playerLinks || {}).forEach(([norm, pid]) => {
    if (pid == null) return;
    (linkByPlayerId[String(pid)] = linkByPlayerId[String(pid)] || []).push(norm);
  });

  const rows = (rosterPlayers || []).map((p) => {
    const auto = resolveMatchTargets(p);
    const linked = linkByPlayerId[String(p.id)] || [];
    const targets = [...new Set([...auto, ...linked])];

    const owners = leagueIndex.filter((t) => targets.some((n) => t.byNorm.has(n)));
    const costs = owners
      .map((t) => targets.reduce((v, n) => (t.byNorm.has(n) ? t.byNorm.get(n) : v), null))
      .filter((v) => v != null);
    const avgCost = costs.length ? costs.reduce((s, v) => s + v, 0) / costs.length : null;
    const matchedNorms = targets.filter((n) => leagueIndex.some((t) => t.byNorm.has(n)));

    return {
      id: p.id,
      name: p.name,
      team: p.team,
      role: p.role,
      credits: Number(p.credits || 0),
      matchedNorms,
      matched: owners.length > 0,
      count: owners.length,
      ownerTeams: owners.map((o) => o.team),
      ownedByMe: owners.some((o) => o.isMine),
      avgCost,
      minCost: costs.length ? Math.min(...costs) : null,
      maxCost: costs.length ? Math.max(...costs) : null,
    };
  });

  return { rows, totalTeams, othersCount };
}

// ─── Statistiche di lega ─────────────────────────────────────────────────
// metaByNorm: Map(normName → { role: 'P'|'D'|'C'|'A', value }) opzionale
//             (arricchimento ruolo/valore dai giocatori FantApp).
export function buildLeagueStats({
  leagueTeams = {},
  totals = {},
  rosterPlayers = [],
  myTeamName = '',
  metaByNorm = new Map(),
  ownedNorms = null, // Set dei norm effettivamente in rosa (post-override); se null si ricava dai match automatici
}) {
  const myNorm = normalizeForMatch(myTeamName || '');
  const teamEntries = Object.entries(leagueTeams);
  const teamCount = teamEntries.length;

  // Spesa per squadra: dalla riga "totale" se c'è, altrimenti somma dei costi.
  const spendByTeam = teamEntries
    .map(([team, players]) => ({
      team,
      total: Number.isFinite(Number(totals[team]))
        ? Number(totals[team])
        : (players || []).reduce((s, p) => s + Number(p.cost || 0), 0),
      players: (players || []).length,
      isMine: Boolean(myNorm) && normalizeForMatch(team) === myNorm,
    }))
    .sort((a, b) => b.total - a.total);

  const spends = spendByTeam.map((t) => t.total).filter((v) => v > 0);
  const creditsInPlay = spends.reduce((s, v) => s + v, 0);
  const avgSpend = spends.length ? Math.round(creditsInPlay / spends.length) : 0;
  const minSpend = spends.length ? Math.min(...spends) : 0;
  const maxSpend = spends.length ? Math.max(...spends) : 0;
  const topSpender = spendByTeam[0] || null;
  const bottomSpender = [...spendByTeam].reverse().find((t) => t.total > 0) || null;
  const mySpendRow = spendByTeam.find((t) => t.isMine);
  const playerCounts = spendByTeam.map((t) => t.players).sort((a, b) => a - b);
  const playersPerTeam = playerCounts[Math.floor(playerCounts.length / 2)] || 0;
  const totalSlots = spendByTeam.reduce((s, t) => s + t.players, 0);
  const avgPlayerPrice = totalSlots ? creditsInPlay / totalSlots : 0;

  // Possesso per giocatore (tutta la lega).
  const owners = new Map(); // norm → { name, teams:Set, costs:[], byTeam:Map }
  let priciestSingle = null; // { name, cost, team }
  teamEntries.forEach(([team, players]) => {
    (players || []).forEach((p) => {
      const n = normalizeForMatch(p.name);
      if (!n) return;
      const cost = Number(p.cost || 0);
      if (!owners.has(n)) owners.set(n, { name: p.name, teams: new Set(), costs: [] });
      const o = owners.get(n);
      o.teams.add(team);
      o.costs.push(cost);
      if (!priciestSingle || cost > priciestSingle.cost) priciestSingle = { name: p.name, cost, team };
    });
  });

  const ownership = [...owners.entries()]
    .map(([norm, o]) => {
      const count = o.teams.size;
      const meta = metaByNorm.get(norm);
      return {
        norm,
        name: o.name,
        role: meta?.role || null,
        listValue: meta?.value ?? null,
        count,
        pct: teamCount ? count / teamCount : 0,
        avgCost: o.costs.length ? o.costs.reduce((s, v) => s + v, 0) / o.costs.length : 0,
        minCost: o.costs.length ? Math.min(...o.costs) : 0,
        maxCost: o.costs.length ? Math.max(...o.costs) : 0,
        spread: o.costs.length ? Math.max(...o.costs) - Math.min(...o.costs) : 0,
        teams: [...o.teams],
      };
    })
    .sort((a, b) => b.count - a.count || b.avgCost - a.avgCost);

  const totalDistinct = ownership.length;
  const exclusive = ownership.filter((o) => o.count === 1);
  const exclusiveCount = exclusive.length;
  const ubiquitousCount = ownership.filter((o) => o.pct >= 0.75).length;
  const mostContested = ownership[0] || null;

  const topExpensive = [...ownership]
    .filter((o) => o.count >= 3)
    .sort((a, b) => b.avgCost - a.avgCost)
    .slice(0, 15);
  const priciestAvg = topExpensive[0] || null;

  // Chi "scommette" di più: squadre con più giocatori esclusivi in rosa.
  const exclusiveByTeam = {};
  exclusive.forEach((o) => {
    const t = o.teams[0];
    if (!exclusiveByTeam[t]) exclusiveByTeam[t] = [];
    exclusiveByTeam[t].push({
      name: o.name,
      role: metaByNorm.get(o.norm)?.role || null,
      cost: o.minCost || 0,
    });
  });
  const mostExclusiveTeams = Object.entries(exclusiveByTeam)
    .map(([team, list]) => ({
      team,
      n: list.length,
      isMine: Boolean(myNorm) && normalizeForMatch(team) === myNorm,
      players: [...list].sort((a, b) => b.cost - a.cost),
    }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 8);

  // Spesa media di lega per reparto (solo se abbiamo il ruolo per abbastanza spesa).
  const bucketSpend = { P: 0, D: 0, C: 0, A: 0 };
  let attributedSpend = 0;
  owners.forEach((o, norm) => {
    const role = metaByNorm.get(norm)?.role;
    if (!role) return;
    const bucket = roleBucketOf(role);
    const sum = o.costs.reduce((s, v) => s + v, 0);
    bucketSpend[bucket] += sum;
    attributedSpend += sum;
  });
  const spendByBucket = creditsInPlay
    ? {
      coverage: attributedSpend / creditsInPlay,
      pct: {
        P: attributedSpend ? bucketSpend.P / attributedSpend : 0,
        D: attributedSpend ? bucketSpend.D / attributedSpend : 0,
        C: attributedSpend ? bucketSpend.C / attributedSpend : 0,
        A: attributedSpend ? bucketSpend.A / attributedSpend : 0,
      },
    }
    : null;

  // Norm dei giocatori che ho in rosa (post-override se passati, altrimenti dai match automatici).
  const myTargets = ownedNorms instanceof Set && ownedNorms.size
    ? ownedNorms
    : (() => {
      const s = new Set();
      rosterPlayers.forEach((p) => resolveMatchTargets(p).forEach((n) => s.add(n)));
      return s;
    })();

  // Squadre più simili alla mia + i giocatori in comune.
  const similarTeams = teamEntries
    .filter(([team]) => !(myNorm && normalizeForMatch(team) === myNorm))
    .map(([team, players]) => {
      const shared = (players || []).filter((p) => myTargets.has(normalizeForMatch(p.name)));
      const union = (players || []).length + myTargets.size - shared.length;
      return {
        team,
        shared: shared.length,
        jaccard: union ? shared.length / union : 0,
        players: shared
          .map((p) => ({ name: p.name, cost: Number(p.cost || 0), role: metaByNorm.get(normalizeForMatch(p.name))?.role || null }))
          .sort((a, b) => b.cost - a.cost),
      };
    })
    .filter((t) => t.shared > 0)
    .sort((a, b) => b.shared - a.shared || b.jaccard - a.jaccard)
    .slice(0, 8);

  // Giocatori più presi nella lega che NON ho in rosa.
  const missedPopular = ownership
    .filter((o) => !myTargets.has(o.norm) && o.count >= 2)
    .slice(0, 20)
    .map((o) => ({
      norm: o.norm, name: o.name, role: o.role, count: o.count, pct: o.pct, avgCost: o.avgCost,
    }));

  return {
    teamCount,
    playersPerTeam,
    totalSlots,
    totalDistinct,
    creditsInPlay,
    avgSpend,
    minSpend,
    maxSpend,
    avgPlayerPrice,
    topSpender,
    bottomSpender,
    mySpend: mySpendRow ? mySpendRow.total : null,
    myTeamMatched: Boolean(mySpendRow),
    spendByTeam,
    ownership,
    topExpensive,
    priciestAvg,
    priciestSingle,
    mostContested,
    exclusiveCount,
    ubiquitousCount,
    mostExclusiveTeams,
    spendByBucket,
    similarTeams,
    missedPopular,
  };
}
